import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Task, TaskStatus, TaskFilters, TaskSort } from '@/types';

export interface TasksState {
  items: Task[];
  selectedTask: Task | null;
  isLoading: boolean;
  error: string | null;
  // Optimistic update tracking
  pendingUpdates: string[];
}

const initialState: TasksState = {
  items: [],
  selectedTask: null,
  isLoading: false,
  error: null,
  pendingUpdates: [],
};

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setTasks: (state, action: PayloadAction<Task[]>) => {
      state.items = action.payload;
      state.isLoading = false;
    },
    addTask: (state, action: PayloadAction<Task>) => {
      state.items.unshift(action.payload);
    },
    updateTask: (state, action: PayloadAction<Task>) => {
      const index = state.items.findIndex((t) => t.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.selectedTask?.id === action.payload.id) {
        state.selectedTask = action.payload;
      }
    },
    updateTaskStatus: (
      state,
      action: PayloadAction<{ taskId: string; status: TaskStatus }>
    ) => {
      const task = state.items.find((t) => t.id === action.payload.taskId);
      if (task) {
        task.status = action.payload.status;
        if (action.payload.status === TaskStatus.COMPLETED) {
          task.completedAt = new Date().toISOString();
        }
      }
    },
    removeTask: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((t) => t.id !== action.payload);
      if (state.selectedTask?.id === action.payload) {
        state.selectedTask = null;
      }
    },
    setSelectedTask: (state, action: PayloadAction<Task | null>) => {
      state.selectedTask = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    reorderTasks: (
      state,
      action: PayloadAction<{ sourceIndex: number; destinationIndex: number }>
    ) => {
      const { sourceIndex, destinationIndex } = action.payload;
      const [removed] = state.items.splice(sourceIndex, 1);
      state.items.splice(destinationIndex, 0, removed);
      // Update order property
      state.items.forEach((task, index) => {
        task.order = index;
      });
    },
    addPendingUpdate: (state, action: PayloadAction<string>) => {
      if (!state.pendingUpdates.includes(action.payload)) {
        state.pendingUpdates.push(action.payload);
      }
    },
    removePendingUpdate: (state, action: PayloadAction<string>) => {
      state.pendingUpdates = state.pendingUpdates.filter(
        (id) => id !== action.payload
      );
    },
  },
});

export const {
  setTasks,
  addTask,
  updateTask,
  updateTaskStatus,
  removeTask,
  setSelectedTask,
  setLoading,
  setError,
  reorderTasks,
  addPendingUpdate,
  removePendingUpdate,
} = tasksSlice.actions;

// Selectors
export const selectAllTasks = (state: { tasks: TasksState }) => state.tasks.items;
export const selectSelectedTask = (state: { tasks: TasksState }) =>
  state.tasks.selectedTask;
export const selectTasksLoading = (state: { tasks: TasksState }) =>
  state.tasks.isLoading;
export const selectTaskById = (taskId: string) => (state: { tasks: TasksState }) =>
  state.tasks.items.find((t) => t.id === taskId);

// Derived selectors
export const selectTodaysTasks = (state: { tasks: TasksState }) => {
  const today = new Date().toISOString().split('T')[0];
  return state.tasks.items.filter((task) => {
    const dueDate = task.dueDate?.split('T')[0];
    return (
      dueDate === today ||
      (task.status !== TaskStatus.COMPLETED && dueDate && dueDate < today)
    );
  });
};

export const selectOverdueTasks = (state: { tasks: TasksState }) => {
  const today = new Date().toISOString().split('T')[0];
  return state.tasks.items.filter((task) => {
    const dueDate = task.dueDate?.split('T')[0];
    return (
      task.status !== TaskStatus.COMPLETED &&
      task.status !== TaskStatus.CANCELLED &&
      dueDate &&
      dueDate < today
    );
  });
};

export const selectTasksByStatus = (status: TaskStatus) => (state: { tasks: TasksState }) =>
  state.tasks.items.filter((task) => task.status === status);

export const selectTasksByProject = (projectId: string) => (state: { tasks: TasksState }) =>
  state.tasks.items.filter((task) => task.projectId === projectId);

export default tasksSlice.reducer;
